import { CSSProperties, ReactNode, useId } from 'react';

type VisualKind = 'store' | 'pin' | 'signal' | 'ads' | 'topic';

const markerPositions = [
  [86, 72], [286, 62], [242, 148], [112, 166], [330, 130], [54, 142], [196, 58],
];

function VisualGlyph({ kind }: { kind: VisualKind }) {
  const paths: Record<VisualKind, ReactNode> = {
    store: <><path d="M5 10h14l-1.2-4H6.2L5 10Z"/><path d="M6.5 10v8h11v-8M10 18v-4h4v4"/></>,
    pin: <><path d="M12 21s6-5.8 6-11a6 6 0 1 0-12 0c0 5.2 6 11 6 11Z"/><circle cx="12" cy="10" r="2"/></>,
    signal: <><circle cx="12" cy="12" r="3"/><circle cx="12" cy="12" r="7"/><path d="M12 2v2m0 16v2M2 12h2m16 0h2"/></>,
    ads: <><path d="M4 10v4l11 4V6L4 10Z"/><path d="M15 9h2.5a2.5 2.5 0 0 1 0 5H15M6 15l1 4"/></>,
    topic: <><path d="M6 7h12M4 12h16M7 17h10"/><circle cx="12" cy="12" r="8"/></>,
  };
  return <svg className="market-glyph" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{paths[kind]}</svg>;
}

export function ZoneVisual({
  radiusLabel,
  markers = 5,
  title = 'Area osservata',
  compact = false,
}: {
  radiusLabel: string;
  markers?: number;
  title?: string;
  compact?: boolean;
}) {
  const patternId = useId().replace(/:/g, '');
  const visibleMarkers = markerPositions.slice(0, Math.max(0, Math.min(markers, markerPositions.length)));
  return <figure className={`zone-visual${compact ? ' is-compact' : ''}`} aria-label={`${title}, raggio ${radiusLabel}`}>
    <svg viewBox="0 0 390 230" role="img" aria-hidden="true" preserveAspectRatio="xMidYMid slice">
      <defs>
        <pattern id={patternId} width="44" height="44" patternUnits="userSpaceOnUse" patternTransform="rotate(18)">
          <path d="M0 10h44M12 0v44" fill="none" stroke="currentColor" strokeOpacity=".11" strokeWidth="1"/>
          <rect x="17" y="16" width="12" height="9" rx="1.5" fill="currentColor" fillOpacity=".06"/>
        </pattern>
      </defs>
      <rect width="390" height="230" fill={`url(#${patternId})`}/>
      <path className="zone-road" d="M-20 184C66 150 87 118 170 116s113-46 240-30M24-10c28 68 80 91 121 142s82 74 134 112M264-20c-20 58-7 102 22 142s44 63 60 125"/>
      <circle className="zone-radius" cx="195" cy="117" r="84"/>
      <circle className="zone-radius-inner" cx="195" cy="117" r="45"/>
      {visibleMarkers.map(([x, y], index) => <g className="zone-marker" key={`${x}-${y}`} transform={`translate(${x} ${y})`}>
        <circle r="14"/><path d="M-4 0h8M0-4v8"/>{index === 0 && <circle className="zone-marker-pulse" r="20"/>}
      </g>)}
      <g className="zone-center" transform="translate(195 117)"><path d="M0 22s17-17 17-31A17 17 0 1 0-17-9C-17 5 0 22 0 22Z"/><circle cy="-9" r="5"/></g>
    </svg>
    <figcaption><span><VisualGlyph kind="pin"/><strong>{title}</strong></span><b>{radiusLabel}</b></figcaption>
  </figure>;
}

export function ScoreArc({ value, label, detail }: { value: number; label: string; detail?: string }) {
  const score = Math.max(0, Math.min(100, Math.round(value)));
  return <div className="score-arc" role="img" aria-label={`${label}: ${score} su 100`}>
    <svg viewBox="0 0 220 124" aria-hidden="true">
      <path className="score-arc-track" pathLength="100" d="M18 108a92 92 0 0 1 184 0"/>
      <path className="score-arc-value" pathLength="100" strokeDasharray="100" strokeDashoffset={100 - score} d="M18 108a92 92 0 0 1 184 0"/>
    </svg>
    <div><strong>{score}</strong><span>/100</span><small>{label}</small>{detail && <em>{detail}</em>}</div>
  </div>;
}

export function MetricBars({ value, max = 100, label }: { value: number; max?: number; label: string }) {
  const safeMax = Math.max(1, max);
  const ratio = Math.max(0, Math.min(1, value / safeMax));
  const bars = Array.from({ length: 7 }, (_, index) => index + 1);
  const active = Math.max(value > 0 ? 1 : 0, Math.round(ratio * bars.length));
  return <div className="metric-bars" role="img" aria-label={`${label}: ${Math.round(value)} su ${Math.round(max)}`}>
    {bars.map((bar) => <i key={bar} className={bar <= active ? 'is-active' : undefined} style={{ '--bar': bar } as CSSProperties}/>) }
  </div>;
}

export function SignalComparison({ previous, current, label = 'Volume raccolto' }: { previous: number; current: number; label?: string }) {
  const max = Math.max(previous, current, 1);
  const delta = previous > 0 ? Math.round(((current - previous) / previous) * 100) : current > 0 ? null : 0;
  return <div className="signal-comparison" aria-label={`${label}: ${current} ora, ${previous} nel periodo precedente`}>
    <div className="signal-orbit" aria-hidden="true"><span/><span/><span/><i/></div>
    <div className="signal-comparison-copy"><small>{label}</small><strong>{delta == null ? 'Nuovo' : `${delta > 0 ? '+' : ''}${delta}%`}</strong><span>{current.toLocaleString('it-IT')} ora · {previous.toLocaleString('it-IT')} prima</span></div>
    <div className="signal-columns" aria-hidden="true"><i style={{ height: `${Math.max(12, previous / max * 100)}%` }}/><i className="is-current" style={{ height: `${Math.max(12, current / max * 100)}%` }}/></div>
  </div>;
}

export function SignalPlot({ points, label }: { points: { label: string; value: number }[]; label: string }) {
  const gradientId = useId().replace(/:/g, '');
  const values = points.slice(0, 7);
  if (values.length < 2) return null;
  const max = Math.max(1, ...values.map((point) => point.value));
  const coordinates = values.map((point, index) => ({
    ...point,
    x: 18 + (index / (values.length - 1)) * 284,
    y: 112 - (point.value / max) * 82,
  }));
  const line = coordinates.map((point) => `${point.x},${point.y}`).join(' ');
  const area = `18,118 ${line} 302,118`;
  return <figure className="signal-plot" aria-label={label}>
    <header><strong>{label}</strong><span>{values.length} segnali</span></header>
    <svg viewBox="0 0 320 132" role="img" aria-hidden="true">
      <defs><linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor="currentColor" stopOpacity=".22"/><stop offset="1" stopColor="currentColor" stopOpacity=".02"/></linearGradient></defs>
      <path className="signal-plot-grid" d="M18 34h284M18 76h284M18 118h284"/>
      <polygon points={area} fill={`url(#${gradientId})`}/>
      <polyline className="signal-plot-line" points={line}/>
      {coordinates.map((point) => <circle key={point.label} cx={point.x} cy={point.y} r="4"/>)}
    </svg>
    <figcaption><span>{values[0].label}</span><span>{values.at(-1)?.label}</span></figcaption>
  </figure>;
}

export function VisualBadge({ kind, children }: { kind: VisualKind; children?: ReactNode }) {
  return <span className={`visual-badge is-${kind}`}><VisualGlyph kind={kind}/>{children && <span>{children}</span>}</span>;
}

export function TimeLayersVisual({ points }: { points: number }) {
  const layers = Math.max(1, Math.min(4, points));
  return <figure className="time-layers-visual" aria-label={`${points} rilevazioni storiche disponibili`}>
    <svg viewBox="0 0 360 270" role="img" aria-hidden="true">
      {Array.from({ length: 4 }, (_, index) => {
        const y = 38 + index * 48;
        const active = index >= 4 - layers;
        return <g key={y} className={active ? 'is-active' : undefined}>
          <path d={`M60 ${y + 28} 180 ${y - 18} 300 ${y + 28} 180 ${y + 74}Z`}/>
          <path className="time-grid" d={`M90 ${y + 17}l120 46m-30-81L300 ${y + 28}M120 ${y + 51} 240 ${y + 5}`}/>
          {active && <><rect x={118 + index * 24} y={y + 9} width="18" height="24" rx="2"/><path className="time-building" d={`M${118 + index * 24} ${y + 9}l9-7 9 7`}/></>}
        </g>;
      })}
      <polyline points="72,240 112,201 146,182 178,135 218,111 252,65 292,38"/>
      {[['72','240'], ['112','201'], ['146','182'], ['178','135'], ['218','111'], ['252','65'], ['292','38']].map(([cx, cy]) => <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="5"/>)}
    </svg>
    <figcaption><strong>L’evoluzione della zona</strong><span>{points > 1 ? `${points} censimenti confrontabili` : 'Prima rilevazione disponibile'}</span></figcaption>
  </figure>;
}
